/**
 * The brand directory and readiness reads (FRS-49, ORDR-117).
 *
 * THE PARSER IS EXPLICIT AND REFUSES, IT DOES NOT TOLERATE. Every documented
 * key is read by name, and a 200 missing one is `failed` with a warning naming
 * the key. A tolerant parser that dropped the bad row would turn a contract
 * change into a silently shorter list, which is exactly the "location I know
 * exists is missing" failure FR-SEL-016 forbids.
 *
 * `request`, not `getJson`, so a 400 (the query was refused) and a 404 (not a
 * brand) can be told apart.
 */

import type { ApiClient } from "../api/client.js";
import type {
  Candidate,
  Directory,
  DirectoryQuery,
  DirectoryResult,
  ModeState,
  OpenState,
  Readiness,
  Revalidation,
} from "./types.js";

type Obj = Record<string, unknown>;

/** Thrown only inside this module, and always caught by `readDirectory`. */
class ContractBreak extends Error {}

function obj(value: unknown, where: string): Obj {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ContractBreak(`${where} is not an object`);
  }
  return value as Obj;
}

function key(source: Obj, name: string, where: string): unknown {
  if (!(name in source)) {
    throw new ContractBreak(`${where}.${name} is missing`);
  }
  return source[name];
}

function str(source: Obj, name: string, where: string): string {
  const value = key(source, name, where);
  if (typeof value !== "string") throw new ContractBreak(`${where}.${name} is not a string`);
  return value;
}

function strOrNull(source: Obj, name: string, where: string): string | null {
  const value = key(source, name, where);
  if (value !== null && typeof value !== "string") throw new ContractBreak(`${where}.${name} is not a string or null`);
  return value;
}

function bool(source: Obj, name: string, where: string): boolean {
  const value = key(source, name, where);
  if (typeof value !== "boolean") throw new ContractBreak(`${where}.${name} is not a boolean`);
  return value;
}

function list(source: Obj, name: string, where: string): unknown[] {
  const value = key(source, name, where);
  if (!Array.isArray(value)) throw new ContractBreak(`${where}.${name} is not a list`);
  return value;
}

function openState(source: Obj, name: string, where: string): OpenState {
  const value = str(source, name, where);
  if (value !== "OPEN" && value !== "CLOSING_SOON" && value !== "CLOSED") {
    throw new ContractBreak(`${where}.${name} is not a known open state`);
  }
  return value;
}

function parseMode(raw: unknown, where: string): ModeState {
  const m = obj(raw, where);
  return {
    mode: str(m, "mode", where),
    openState: openState(m, "open_state", where),
    acceptsOrders: bool(m, "accepts_orders", where),
    reason: strOrNull(m, "reason", where),
    closureReason: str(m, "closure_reason", where),
    nextOpensAt: strOrNull(m, "next_opens_at", where),
    closesAt: strOrNull(m, "closes_at", where),
    lastOrderAt: strOrNull(m, "last_order_at", where),
  };
}

function parseCandidate(raw: unknown, where: string): Candidate {
  const l = obj(raw, where);
  const address = obj(key(l, "address_summary", where), `${where}.address_summary`);
  const status = str(l, "status", where);
  if (status !== "ACTIVE" && status !== "SUSPENDED") {
    throw new ContractBreak(`${where}.status is not a known status`);
  }
  const modeAvailable = key(l, "mode_available", where);
  if (modeAvailable !== null && typeof modeAvailable !== "boolean") {
    throw new ContractBreak(`${where}.mode_available is not a boolean or null`);
  }
  const distance = key(l, "distance_m", where);
  if (distance !== null && typeof distance !== "number") {
    throw new ContractBreak(`${where}.distance_m is not a number or null`);
  }
  const enabledModes = list(l, "enabled_modes", where).map((mode, index) => {
    if (typeof mode !== "string") throw new ContractBreak(`${where}.enabled_modes[${index}] is not a string`);
    return mode;
  });
  return {
    locationNodeId: str(l, "location_node_id", where),
    siteId: str(l, "site_id", where),
    displayName: str(l, "display_name", where),
    address: {
      line1: strOrNull(address, "line1", `${where}.address_summary`),
      city: strOrNull(address, "city", `${where}.address_summary`),
      region: strOrNull(address, "region", `${where}.address_summary`),
    },
    timezone: str(l, "timezone", where),
    status,
    openState: openState(l, "open_state", where),
    nextOpensAt: strOrNull(l, "next_opens_at", where),
    enabledModes,
    modeAvailable,
    unavailableReason: strOrNull(l, "unavailable_reason", where),
    distanceM: distance,
    modes: list(l, "modes", where).map((mode, index) => parseMode(mode, `${where}.modes[${index}]`)),
  };
}

function parseDirectory(body: unknown): Directory {
  const data = obj(key(obj(body, "body"), "data", "body"), "data");
  const revalidation = key(data, "revalidation", "data");
  let parsedRevalidation: Revalidation[] | null = null;
  if (revalidation !== null) {
    if (!Array.isArray(revalidation)) throw new ContractBreak("data.revalidation is not a list or null");
    parsedRevalidation = revalidation.map((raw, index) => {
      const where = `data.revalidation[${index}]`;
      const r = obj(raw, where);
      return { locationNodeId: str(r, "location_node_id", where), outcome: str(r, "outcome", where) };
    });
  }
  str(data, "brand_node_id", "data");
  str(data, "at", "data");
  return {
    mode: strOrNull(data, "mode", "data"),
    resolvesFromAddress: bool(data, "resolves_from_address", "data"),
    truncated: bool(data, "truncated", "data"),
    locations: list(data, "locations", "data").map((raw, index) => parseCandidate(raw, `data.locations[${index}]`)),
    revalidation: parsedRevalidation,
  };
}

export async function readDirectory(
  client: ApiClient,
  brandNodeId: string,
  query: DirectoryQuery,
): Promise<DirectoryResult> {
  const params = new URLSearchParams();
  if (query.mode !== undefined) params.set("mode", query.mode);
  if (query.near !== undefined) params.set("near", query.near);
  if (query.q !== undefined) params.set("q", query.q);
  if (query.ids !== undefined) params.set("ids", query.ids.join(","));
  const search = params.toString();
  const path = `/ordering/brands/${encodeURIComponent(brandNodeId)}/locations/${search === "" ? "" : `?${search}`}`;

  const response = await client.request(path);
  if (response.status === 404) return { kind: "not-found" };
  if (response.status === 400) return { kind: "refused", correlationId: response.correlationId };
  if (response.status !== 200) return { kind: "failed", correlationId: response.correlationId };
  try {
    return { kind: "ok", directory: parseDirectory(response.body) };
  } catch (caught) {
    if (!(caught instanceof ContractBreak)) throw caught;
    console.warn(`[locations] brand directory answer refused (${response.correlationId}): ${caught.message}`);
    return { kind: "failed", correlationId: response.correlationId };
  }
}

/**
 * One location's readiness. Any failure is `null`: an estimate's absence is
 * not a closure, and the row simply shows none.
 */
export async function readReadiness(
  client: ApiClient,
  locationNodeId: string,
  mode: string | null,
): Promise<Readiness | null> {
  const search = mode === null ? "" : `?${new URLSearchParams({ mode }).toString()}`;
  const response = await client.request(`/ordering/locations/${encodeURIComponent(locationNodeId)}/readiness/${search}`);
  if (response.status !== 200) return null;
  try {
    const data = obj(key(obj(response.body, "body"), "data", "body"), "data");
    const seconds = key(data, "ready_in_seconds", "data");
    return {
      state: str(data, "state", "data"),
      reason: strOrNull(data, "reason", "data"),
      readyInSeconds: typeof seconds === "number" ? seconds : null,
    };
  } catch (caught) {
    if (!(caught instanceof ContractBreak)) throw caught;
    console.warn(`[locations] readiness answer refused (${response.correlationId}): ${caught.message}`);
    return null;
  }
}
